import { IconBan, IconCrown, IconSettings, IconShoppingCart, TablerIcon } from '@tabler/icons'
import { UtilityModal, ModalBody } from '../components'

interface DataType {
  pagesLimit?: number
  autoresponderLimit?: number
  formsLimit?: number
  journeyId?: number
  used?: number | null
  limit?: number | null
}

export enum ERROR_TYPES {
  dripCampaignsDisabled = 'dripCampaignsDisabled',
  landingPagesDisabled = 'landingPagesDisabled',
  landingPagesLimitExceeded = 'landingPagesLimitExceeded',
  landingPagesAbTestDisabled = 'landingPagesAbTestDisabled',
  broadcastsDisabled = 'broadcastsDisabled',
  emailsDisabled = 'emailsDisabled',
  autorespondersLimitExceeded = 'autorespondersLimitExceeded',
  formsLimitExceeded = 'formsLimitExceeded',
  emailOwnershipDoesNotExist = 'emailOwnershipDoesNotExist',
  journeysLimitExceeded = 'journeysLimitExceeded',
  automationDisabled = 'automationDisabled',
  widgetsAbTestDisabled = 'widgetsAbTestDisabled',
  activeCampaignLimitExceeded = 'activeCampaignLimitExceeded',
  contactLimitExceeded = 'contactLimitExceeded'
}

type actions = "danger" | "default" | "primary" | "success" | "warning";

export interface ErrorTypeReturn {
  buttons: {
    label: string;
    reactIntlId: string
    action: string;
    onClick: () => void;
  }[];
  action: actions
  icon: TablerIcon;
  title: string;
  description: string;
  reactIntlTitleId?: string;
  reactIntlDescriptionId?: string;
  reactIntlValues?: any;
}

export const ErrorModal = (alertConfig?: ErrorTypeReturn | null) => {
  if (!alertConfig) return
  return UtilityModal({
    id:"error-modal",
    body: props => 
      <ModalBody 
      {...props} 
      {...alertConfig} 
      icon={<alertConfig.icon size="20px" />} 
      />
  })
}


const ErrorType = ({ type , data, history, siteId }:{ type: ERROR_TYPES | string, data?: DataType, history?: any, siteId?: number }): ErrorTypeReturn | undefined => {

  const upgradeAlert = {
    action: "primary" as actions,
    buttons: [
      { label: 'Upgrade', action: 'primary', onClick: () => window?.pricing?.show(), reactIntlId: "global.button.upgrade" },
    ],
    icon: IconCrown,
  }

  const contactAlert = {
    description: 'Your account may have been flagged. Please contact support to resolve this.',
    reactIntlDescriptionId: "errorType.contactAlert.description",
    action: "danger" as actions,
    icon: IconBan,
    buttons: [
      { label: 'Contact Support', action: 'primary', onClick: () => window.location.href = '/contact', reactIntlId: "errorType.contactSupport" },
    ]
  }

  switch(type) {
    case ERROR_TYPES.dripCampaignsDisabled:
      return {
        title: 'Drip Campaigns',
        reactIntlTitleId: "errorType.dripCampaigns.title",
        description: `Drip Campaigns are a premium feature. Please upgrade your account.`,
        reactIntlDescriptionId: "errorType.dripCampaigns.description",
        ...upgradeAlert,
        buttons: [
          { label: 'Upgrade', action: 'primary', onClick: () => window?.pricing?.show({ product: 'emails', minimumPlanLevelRequired: 2, highlightFeatures: 'drip_campaigns' }), reactIntlId: "global.button.upgrade" },
        ],
      }
    case ERROR_TYPES.landingPagesDisabled:
      return {
        title: `Your landing pages have been disabled.`,
        reactIntlTitleId: "errorType.landingPagesDisabled.title",
        ...contactAlert
      }
    case ERROR_TYPES.landingPagesLimitExceeded:
      const { pagesLimit } = data || {}
      return {
        title: `Landing page limit exceeded.`,
        reactIntlTitleId: "errorType.landingPagesLimitExceeded.title",
        description: `You can only create ${pagesLimit} landing ${pagesLimit && pagesLimit > 1 ? "pages" : "page"} in your current plan. Please upgrade for more landing pages.`,
        reactIntlValues: {
          pagesLimit: pagesLimit || 0,
          pageText: `${pagesLimit && pagesLimit > 1 ? "pages" : "page"}`
        },
        reactIntlDescriptionId: "errorType.landingPagesLimitExceeded.description",
        ...upgradeAlert
      }
    case ERROR_TYPES.landingPagesAbTestDisabled:
      return {
        title: `A/B Tests`,
        reactIntlTitleId: "errorType.landingPagesAbTestDisabled.title",
        description: `Landing Page Split tests are a premium feature. Please upgrade your account.`,
        reactIntlDescriptionId: "errorType.landingPagesAbTestDisabled.description",
        ...upgradeAlert,
        buttons: [
          { label: 'Upgrade', action: 'primary', onClick: () => window?.pricing?.show({minimumPlanLevelRequired: 2, highlightFeatures: 'ab_testing'}), reactIntlId: "global.button.upgrade" },
        ],
      }
    case ERROR_TYPES.broadcastsDisabled:
      return {
        title: `Your broadcast emails have been disabled.`,
        reactIntlTitleId: "errorType.broadcastsDisabled.title",
        ...contactAlert
      }
    case ERROR_TYPES.emailsDisabled:
      return {
        title: `Your emails have been disabled.`,
        reactIntlTitleId: "errorType.emailsDisabled.title",
        ...contactAlert
      }
    case ERROR_TYPES.autorespondersLimitExceeded:
      const { autoresponderLimit } = data || {}
      return {
        title: `Autoresponders limit exceeded.`,
        reactIntlTitleId: "errorType.autorespondersLimitExceeded.title",
        description: `You can only create ${autoresponderLimit} ${autoresponderLimit && autoresponderLimit > 1 ? "autoresponders" : "autoresponder"} in your current plan. Please upgrade for more autoresponders.`,
        reactIntlDescriptionId: "errorType.autorespondersLimitExceeded.description",
        reactIntlValues: {
          autoresponderLimit: autoresponderLimit || 0,
          autoresponderText: `${autoresponderLimit && autoresponderLimit > 1 ? "autoresponders" : "autoresponder"}`
        },
        ...upgradeAlert
      }
    case ERROR_TYPES.formsLimitExceeded:
      const { formsLimit } = data || {}
      return {
        title: `Popup forms limit exceeded.`,
        reactIntlTitleId: "errorType.formsLimitExceeded.title",
        description: `You can only create ${formsLimit} ${formsLimit && formsLimit > 1 ? "forms" : "form"} in your current plan. Please upgrade for more forms.`,
        reactIntlDescriptionId: "errorType.formsLimitExceeded.description",
        reactIntlValues: {
          formsLimit: formsLimit || 0,
          formText: `${formsLimit && formsLimit > 1 ? "forms" : "form"}`
        },
        ...upgradeAlert
      }
    case ERROR_TYPES.emailOwnershipDoesNotExist:
      return {
        title: `Sender email address`,
        reactIntlTitleId: "errorType.emailOwnershipDoesNotExist.title",
        action: "primary",
        description: `A verified sender email address is required to start this flow. Would you like to add one now?`,
        reactIntlDescriptionId: "errorType.emailOwnershipDoesNotExist.description",
        buttons: [
          { label: 'Open Settings', action: 'primary', onClick: () => history?.replace({ pathname: `/sites/${siteId}/settings/domains-and-emails` }), reactIntlId: "errorType.openSettings" },
        ],
        icon: IconSettings,
      }
    case ERROR_TYPES.journeysLimitExceeded: 
      const { journeyId } = data || {}
      return {
        title: `Flow already exists`,
        reactIntlTitleId: "errorType.journeysLimitExceeded.title",
        action: "primary",
        description: `You already have a cart abandonment flow in this site. Would you like to open it?`,
        reactIntlDescriptionId: "errorType.journeysLimitExceeded.description",
        buttons: [
          { label: 'Open Flow', action: 'primary', onClick: () => window.location.href = `journeys/${journeyId && journeyId}`, reactIntlId: "errorType.openFlow" },
        ],
        icon: IconShoppingCart,
      }
    case ERROR_TYPES.automationDisabled:
      return {
        title: `Automations`,
        reactIntlTitleId: "errorType.automationDisabled.title",
        description: `Automations are a premium feature. Please upgrade your account.`,
        reactIntlDescriptionId: "errorType.automationDisabled.description",
        ...upgradeAlert,
        buttons: [
          { label: 'Upgrade', action: 'primary', onClick: () => window?.pricing?.show({ product: "emails", minimumPlanLevelRequired: 2, highlightFeatures: 'automation'}), reactIntlId: "global.button.upgrade" },
        ],
      }
    case ERROR_TYPES.activeCampaignLimitExceeded:
      return {
        title: 'Active campaign limit reached',
        reactIntlTitleId: 'errorType.activeCampaignLimitExceeded.title',
        description: 'You can only have one active autoresponder, drip, or automation at a time. Deactivate your current campaign before activating another.',
        reactIntlDescriptionId: 'errorType.activeCampaignLimitExceeded.description',
        action: 'primary' as actions,
        icon: IconBan,
        buttons: [
          {
            label: 'Contact Support',
            action: 'primary',
            onClick: () => window?.Intercom?.('show'),
            reactIntlId: 'errorType.contactSupport',
          },
        ],
      }
    case ERROR_TYPES.contactLimitExceeded: {
      const used = data?.used
      const limit = data?.limit
      return {
        title: 'Contact limit exceeded',
        reactIntlTitleId: 'errorType.contactLimitExceeded.title',
        description: `You have ${used ?? '—'} of ${limit ?? '—'} contacts. Campaign activation is paused until you are back under your plan limit. Remove contacts or contact support.`,
        reactIntlDescriptionId: 'errorType.contactLimitExceeded.description',
        reactIntlValues: { used: used ?? '—', limit: limit ?? '—' },
        action: 'danger' as actions,
        icon: IconBan,
        buttons: [
          {
            label: 'Contact Support',
            action: 'primary',
            onClick: () => window?.Intercom?.('show'),
            reactIntlId: 'errorType.contactSupport',
          },
        ],
      }
    }
    case ERROR_TYPES.widgetsAbTestDisabled:
      return {
        title: `A/B Tests`,
        reactIntlTitleId: "errorType.widgetsAbTestDisabled.title",
        description: `Form Split tests are a premium feature. Please upgrade your account.`,
        reactIntlDescriptionId: "errorType.widgetsAbTestDisabled.description",
        ...upgradeAlert,
        buttons: [
          { label: 'Upgrade', action: 'primary', onClick: () => window?.pricing?.show({minimumPlanLevelRequired: 2, highlightFeatures: 'ab_testing'}), reactIntlId: "global.button.upgrade" },
        ],
      }
    default:
      return undefined
  }
}

export { ErrorType }